// SPDX-FileCopyrightText: Copyright Orangebot, Inc. and Medplum contributors
// SPDX-License-Identifier: Apache-2.0
import { formatHumanName } from '@medplum/core';
import type { BundleEntry, Patient } from '@medplum/fhirtypes';
import { useMedplum } from '@medplum/react';
import {
  CLOUDPAT_OBSERVATION_SYSTEM,
  INLAB_REFERRAL_MODULE_URI,
  isTestPatient,
  PATIENT_REFERRAL_STATUS,
  PATIENT_TRIAGE_STATUS,
  POLARIS_REFERRAL_STATUS_TAG_SYSTEM,
  POLARIS_TESTING_OBS_CATEGORY_SYSTEM,
  POLARIS_TRIAGE_STATUS_TAG_SYSTEM,
  SELF_PAY_TAG_SYSTEM,
  STUDY_XML_CLINICAL_FIELDS,
  TESTING_OBS_CATEGORY,
  type TriageSortState,
} from '@polarissleep/shared';
import { useCallback, useEffect, useMemo, useState } from 'react';

const CLINICAL_OBS_CODES = Object.keys(STUDY_XML_CLINICAL_FIELDS);
type ClinicalObsCode = keyof typeof STUDY_XML_CLINICAL_FIELDS;

export interface TestingQueueEntry {
  patientId: string;
  patientName: string;
  triageStatusCode: string | undefined;
  orderingProviderName?: string;
  diagnosticReportId: string | undefined;
  reportStatus: string | undefined;
  rowVariant: 'hsat' | 'inLabReferral';
  obs: Partial<Record<ClinicalObsCode, number>>;
  isTestPatient: boolean;
}

interface ObsResult {
  valueQuantity?: { value?: number };
}

interface DiagnosticReportResult {
  id?: string;
  status?: string;
}

interface GuidanceResponseResult {
  moduleUri?: string;
}

interface ServiceRequestResult {
  code?: { coding?: { system?: string; code?: string }[] };
  requester?: {
    resource?: {
      name?: { given?: string[]; family?: string }[];
    };
  };
}

interface PatientResult extends Patient {
  hsatReport?: DiagnosticReportResult[];
  GuidanceResponseList?: GuidanceResponseResult[];
  ServiceRequestList?: ServiceRequestResult[];
  [alias: string]: unknown;
}

// Column keys → CLOUDPAT observation codes for server-side value sorting.
const TESTING_OBS_SORT_CODES: Record<string, string> = {
  ahi: 'AHI',
  ahiCentral: 'AHICentral',
  totalSleepTime: 'TotalSleepTime',
  validSleepTime: 'TotalAH_CentralValidSleepTime',
  satBelow88: 'SatBelowEqual88',
  meanNadir: 'MeanNadirDesaturations',
};

/**
 * GraphQL filter value for the Testing Queue null-bucket marker category.
 * Shape: `<POLARIS_TESTING_OBS_CATEGORY_SYSTEM>|patient-latest-testing-nullish`.
 */
const PATIENT_LATEST_TESTING_NULLISH_CATEGORY_FILTER = `${POLARIS_TESTING_OBS_CATEGORY_SYSTEM}|${TESTING_OBS_CATEGORY.patientLatestNullish}`;
const TEST_PATIENT_TAG = `${SELF_PAY_TAG_SYSTEM}|test-patient`;

function addTestPatientExclusion(params: URLSearchParams, hideTestPatients: boolean): void {
  if (hideTestPatients) {
    params.append('_tag:not', TEST_PATIENT_TAG);
  }
}

function mapToEntry(patient: PatientResult): TestingQueueEntry {
  const patientName = patient.name?.[0] ? formatHumanName(patient.name[0]) : 'Unknown Patient';
  const report = patient.hsatReport?.[0];
  const triageTag = patient.meta?.tag?.find((t) => t.system === POLARIS_TRIAGE_STATUS_TAG_SYSTEM);
  const referralStatusTag = patient.meta?.tag?.find((t) => t.system === POLARIS_REFERRAL_STATUS_TAG_SYSTEM);
  const hasInLabReferral =
    patient.GuidanceResponseList?.some((gr) => gr.moduleUri === INLAB_REFERRAL_MODULE_URI) ?? false;
  const rowVariant =
    hasInLabReferral || (referralStatusTag?.code !== undefined && referralStatusTag.code !== PATIENT_REFERRAL_STATUS.na)
      ? 'inLabReferral'
      : 'hsat';

  const obs: Partial<Record<ClinicalObsCode, number>> = {};
  for (const code of CLINICAL_OBS_CODES) {
    const alias = `obs${code}`;
    const value = (patient[alias] as ObsResult[] | undefined)?.[0]?.valueQuantity?.value;
    if (value !== undefined) {
      obs[code as ClinicalObsCode] = value;
    }
  }

  const latestHsatOrder = patient.ServiceRequestList?.find((request) =>
    request.code?.coding?.some((coding) => coding.system === 'http://snomed.info/sct' && coding.code === '276734008')
  );
  const orderingProviderName = latestHsatOrder?.requester?.resource?.name?.[0]
    ? formatHumanName(latestHsatOrder.requester.resource.name[0])
    : undefined;

  return {
    patientId: patient.id ?? '',
    patientName,
    triageStatusCode: triageTag?.code,
    orderingProviderName,
    diagnosticReportId: report?.id,
    reportStatus: report?.status,
    rowVariant,
    obs,
    isTestPatient: isTestPatient(patient),
  };
}

const OBS_FIELDS = CLINICAL_OBS_CODES.map(
  (code) =>
    `obs${code}: ObservationList(_reference: subject code: "${CLOUDPAT_OBSERVATION_SYSTEM}|${code}" _count: 1 _sort: "-_lastUpdated") {
      valueQuantity { value }
    }`
).join('\n          ');

const PATIENT_HYDRATION_FIELDS = `
  id
  name { given family }
  meta { tag { system code } }
  hsatReport: DiagnosticReportList(_reference: subject _count: 1 _sort: "-_lastUpdated") {
    id
    status
  }
  GuidanceResponseList(_reference: subject _count: 10 _sort: "-_lastUpdated") {
    moduleUri
  }
  ServiceRequestList(_reference: subject _count: 5 _sort: "-_lastUpdated") {
    code {
      coding {
        system
        code
      }
    }
    requester {
      resource {
        ... on Practitioner {
          name { given family }
        }
      }
    }
  }
  ${OBS_FIELDS}
`;

interface Options {
  page: number;
  pageSize: number;
  search?: string;
  sort?: TriageSortState;
  hideTestPatients?: boolean;
}

export function useTestingQueue({ page, pageSize, search, sort, hideTestPatients = false }: Options) {
  const medplum = useMedplum();
  const [entries, setEntries] = useState<TestingQueueEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [total, setTotal] = useState(0);

  // Stable primitive key for sort — prevents infinite fetch loop.
  const sortKey = useMemo(() => (sort ? `${sort.column}|${sort.direction}` : null), [sort]);

  // Observation sort branch: obs column, no search.
  const useObsSort = sort !== undefined && sort.column in TESTING_OBS_SORT_CODES && !search;
  // Obs sort + name search coexistence branch.
  const useSortAndSearch = sort !== undefined && sort.column in TESTING_OBS_SORT_CODES && !!search;

  // Shared hydration query builder used by both branches.
  const patientCsvForGraphQL = useCallback(
    (csv: string, count: number): string => `
      query TestingQueueHydration {
        PatientList(_id: "${csv}" _count: ${count}) {
          ${PATIENT_HYDRATION_FIELDS}
        }
      }
    `,
    []
  );

  // biome-ignore lint/correctness/useExhaustiveDependencies: sort tracked via sortKey (stable primitive) — object identity excluded intentionally. useObsSort and useSortAndSearch derived from sort+search.
  const fetchEntries = useCallback(async () => {
    setLoading(true);
    const offset = (page - 1) * pageSize;
    const t = (code: string) => `${POLARIS_TRIAGE_STATUS_TAG_SYSTEM}|${code}`;
    const stageTag = [
      t(PATIENT_TRIAGE_STATUS.hsat),
      t(PATIENT_TRIAGE_STATUS.hsatApprovedNp),
      t(PATIENT_TRIAGE_STATUS.hsatOrdered),
      t(PATIENT_TRIAGE_STATUS.hsatResults),
    ].join(',');

    try {
      if (useSortAndSearch && sort) {
        // ── Obs sort + name search branch ─────────────────────────────────────
        // REST Observation search (with-value bucket scoped via chained predicates)
        // + positive-marker null-bucket query. No exclusion-set pre-fetch.
        const obsCode = TESTING_OBS_SORT_CODES[sort.column] ?? sort.column;
        const codeFilter = `${CLOUDPAT_OBSERVATION_SYSTEM}|${obsCode}`;
        const obsSortParam = sort.direction === 'asc' ? 'value-quantity' : '-value-quantity';

        // 1. Count matching patients.
        const countParams = new URLSearchParams();
        countParams.set('_tag', stageTag);
        countParams.set('name', search ?? '');
        addTestPatientExclusion(countParams, hideTestPatients);
        countParams.set('_summary', 'count');
        const countBundle = await medplum.search('Patient', countParams);
        const totalMatchingCount = countBundle.total ?? 0;

        if (totalMatchingCount === 0) {
          setTotal(0);
          setEntries([]);
          return;
        }

        // 2. Fetch observations sorted by value-quantity.
        const obsParams = new URLSearchParams();
        obsParams.set('code', codeFilter);
        obsParams.set('subject:Patient._tag', stageTag);
        obsParams.set('subject:Patient.name', search ?? '');
        if (hideTestPatients) {
          obsParams.set('subject:Patient._tag:not', TEST_PATIENT_TAG);
        }
        obsParams.set('_sort', obsSortParam);
        obsParams.set('_count', String(pageSize));
        obsParams.set('_offset', String(offset));
        obsParams.set('_total', 'accurate');
        const obsBundle = await medplum.search('Observation', obsParams);
        const rawWithValueCount = obsBundle.total ?? 0;
        const withValuePatientIds: string[] = (obsBundle.entry ?? [])
          .map((e: BundleEntry) => (e.resource as { subject?: { reference?: string } } | undefined)?.subject?.reference)
          .filter((ref: string | undefined): ref is string => !!ref?.startsWith('Patient/'))
          .map((ref: string) => ref.slice('Patient/'.length));

        const clampedWithValueCount = Math.min(rawWithValueCount, totalMatchingCount);
        const nullBucketCount = Math.max(0, totalMatchingCount - clampedWithValueCount);

        // 3. Hydrate with-value patients.
        const withValuePatientsById = new Map<string, PatientResult>();
        if (withValuePatientIds.length > 0) {
          const hydrationResult = await medplum.graphql(patientCsvForGraphQL(withValuePatientIds.join(','), pageSize));
          for (const p of (hydrationResult.data?.PatientList ?? []) as PatientResult[]) {
            if (p.id) {
              withValuePatientsById.set(p.id, p);
            }
          }
        }

        const withValueRows = withValuePatientIds
          .map((id: string) => {
            const p = withValuePatientsById.get(id);
            return p ? mapToEntry(p) : null;
          })
          .filter(Boolean) as TestingQueueEntry[];

        // 4. Null bucket — positive-marker query.
        const pageStart = offset;
        const pageEnd = offset + pageSize;
        const straddlesBoundary = pageStart < clampedWithValueCount && pageEnd > clampedWithValueCount;
        const beyondBoundary = pageStart >= clampedWithValueCount;
        const needsNullBucket = nullBucketCount > 0 && (straddlesBoundary || beyondBoundary);

        let nullRows: TestingQueueEntry[] = [];
        if (needsNullBucket) {
          let nullOffset: number;
          let nullCount: number;
          if (sort.direction === 'desc') {
            nullOffset = Math.max(0, pageStart - clampedWithValueCount);
            nullCount = Math.max(0, Math.min(pageSize - withValueRows.length, nullBucketCount - nullOffset));
          } else {
            nullOffset = pageStart;
            nullCount = pageStart >= nullBucketCount ? 0 : Math.min(pageSize, nullBucketCount - pageStart);
          }
          if (nullCount > 0) {
            // Positive-marker null-bucket query: Observation?category=patient-latest-testing-nullish
            //   &code=<col>&subject:Patient._tag=<stageTag>&subject:Patient.name=<search>
            //   &_sort=-_lastUpdated&_count=<nullCount>&_offset=<nullOffset>
            // No exclusion set needed — markers only exist for patients WITHOUT a real value.
            const nullMarkerParams = new URLSearchParams();
            nullMarkerParams.set('category', PATIENT_LATEST_TESTING_NULLISH_CATEGORY_FILTER);
            nullMarkerParams.set('code', codeFilter);
            nullMarkerParams.set('subject:Patient._tag', stageTag);
            nullMarkerParams.set('subject:Patient.name', search ?? '');
            if (hideTestPatients) {
              nullMarkerParams.set('subject:Patient._tag:not', TEST_PATIENT_TAG);
            }
            nullMarkerParams.set('_sort', '-_lastUpdated');
            nullMarkerParams.set('_count', String(nullCount));
            nullMarkerParams.set('_offset', String(nullOffset));

            const nullMarkerBundle = await medplum.search('Observation', nullMarkerParams);
            const nullPatientIds: string[] = [];
            for (const entry of nullMarkerBundle.entry ?? []) {
              const obs = entry.resource as { subject?: { reference?: string } } | undefined;
              const subjectRef = obs?.subject?.reference;
              if (subjectRef?.startsWith('Patient/')) {
                nullPatientIds.push(subjectRef.slice('Patient/'.length));
              }
            }

            if (nullPatientIds.length > 0) {
              const nullResult = await medplum.graphql(patientCsvForGraphQL(nullPatientIds.join(','), nullCount));
              nullRows = ((nullResult.data?.PatientList ?? []) as PatientResult[]).map(mapToEntry);
            }
          }
        }

        const merged = sort.direction === 'asc' ? [...nullRows, ...withValueRows] : [...withValueRows, ...nullRows];
        setTotal(totalMatchingCount);
        setEntries(hideTestPatients ? merged.filter((e) => !e.isTestPatient) : merged);
        return;
      }

      if (useObsSort && sort) {
        // ── Pure obs sort branch (no search) ──────────────────────────────────
        const obsCode = TESTING_OBS_SORT_CODES[sort.column] ?? sort.column;
        const codeFilter = `${CLOUDPAT_OBSERVATION_SYSTEM}|${obsCode}`;
        const obsSortParam = sort.direction === 'asc' ? 'value-quantity' : '-value-quantity';

        // 1. Get all testing patient count.
        let totalTestingCount: number;
        if (hideTestPatients) {
          const countParams = new URLSearchParams();
          countParams.set('_tag', stageTag);
          addTestPatientExclusion(countParams, true);
          countParams.set('_summary', 'count');
          const countResult = await medplum.search('Patient', countParams);
          totalTestingCount = countResult.total ?? 0;
        } else {
          const allPatientsResult = await medplum.graphql(`
            query TestingAllPatients {
              PatientConnection(_tag: "${stageTag}") { count }
            }
          `);
          totalTestingCount = (allPatientsResult.data?.PatientConnection as { count: number } | undefined)?.count ?? 0;
        }

        if (totalTestingCount === 0) {
          setTotal(0);
          setEntries([]);
          return;
        }

        // 2. Fetch all testing patient IDs for subject scoping (mirrors useIneligibleQueue).
        let allPatientIds: string[];
        if (hideTestPatients) {
          const patientParams = new URLSearchParams();
          patientParams.set('_tag', stageTag);
          addTestPatientExclusion(patientParams, true);
          patientParams.set('_count', String(totalTestingCount));
          const patientsResult = await medplum.search('Patient', patientParams);
          allPatientIds = (patientsResult.entry ?? [])
            .map((entry: BundleEntry) => (entry.resource as Patient | undefined)?.id)
            .filter((id: string | undefined): id is string => Boolean(id));
        } else {
          const patientIdsResult = await medplum.graphql(`
            query TestingPatientIds {
              PatientList(_tag: "${stageTag}" _count: ${totalTestingCount}) {
                id
              }
            }
          `);
          allPatientIds = ((patientIdsResult.data?.PatientList ?? []) as { id?: string }[])
            .map((p) => p.id)
            .filter((id): id is string => Boolean(id));
        }

        if (allPatientIds.length === 0) {
          setTotal(0);
          setEntries([]);
          return;
        }

        const subjectFilter = `subject: "${allPatientIds.map((id) => `Patient/${id}`).join(',')}"`;

        // 3. Fetch observations sorted by value-quantity, scoped to testing patients.
        const obsResult = await medplum.graphql(`
          query TestingObsSorted {
            ObservationList(
              code: "${codeFilter}"
              ${subjectFilter}
              _sort: "${obsSortParam}"
              _count: ${pageSize}
              _offset: ${offset}
            ) {
              subject { reference }
            }
            ObservationConnection(
              code: "${codeFilter}"
              ${subjectFilter}
            ) { count }
          }
        `);
        const observationList = (obsResult.data?.ObservationList ?? []) as { subject?: { reference?: string } }[];
        const rawWithValueCount = (obsResult.data?.ObservationConnection as { count: number } | undefined)?.count ?? 0;

        const withValuePatientIds = observationList
          .map((obs) => obs.subject?.reference)
          .filter((ref): ref is string => !!ref?.startsWith('Patient/'))
          .map((ref) => ref.slice('Patient/'.length));

        const clampedWithValueCount = Math.min(rawWithValueCount, totalTestingCount);
        const nullBucketCount = Math.max(0, totalTestingCount - clampedWithValueCount);

        // 3. Hydrate with-value patients (full obs data).
        const withValuePatientsById = new Map<string, PatientResult>();
        if (withValuePatientIds.length > 0) {
          const hydrationResult = await medplum.graphql(patientCsvForGraphQL(withValuePatientIds.join(','), pageSize));
          for (const p of (hydrationResult.data?.PatientList ?? []) as PatientResult[]) {
            if (p.id) {
              withValuePatientsById.set(p.id, p);
            }
          }
        }

        const withValueRows = withValuePatientIds
          .map((id) => {
            const p = withValuePatientsById.get(id);
            return p ? mapToEntry(p) : null;
          })
          .filter(Boolean) as TestingQueueEntry[];

        // 4. Null bucket — positive-marker query.
        const pageStart = offset;
        const pageEnd = offset + pageSize;
        const straddlesBoundary = pageStart < clampedWithValueCount && pageEnd > clampedWithValueCount;
        const beyondBoundary = pageStart >= clampedWithValueCount;
        const needsNullBucket = nullBucketCount > 0 && (straddlesBoundary || beyondBoundary);

        let nullRows: TestingQueueEntry[] = [];
        if (needsNullBucket) {
          let nullOffset: number;
          let nullCount: number;
          if (sort.direction === 'desc') {
            nullOffset = Math.max(0, pageStart - clampedWithValueCount);
            nullCount = Math.max(0, Math.min(pageSize - withValueRows.length, nullBucketCount - nullOffset));
          } else {
            nullOffset = pageStart;
            nullCount = pageStart >= nullBucketCount ? 0 : Math.min(pageSize, nullBucketCount - pageStart);
          }
          if (nullCount > 0) {
            // Positive-marker null-bucket query: Observation?category=patient-latest-testing-nullish
            //   &code=<col>&subject:Patient._tag=<stageTag>
            //   &_sort=-_lastUpdated&_count=<nullCount>&_offset=<nullOffset>
            // No exclusion set needed — markers only exist for patients WITHOUT a real value.
            const nullMarkerParams = new URLSearchParams();
            nullMarkerParams.set('category', PATIENT_LATEST_TESTING_NULLISH_CATEGORY_FILTER);
            nullMarkerParams.set('code', codeFilter);
            nullMarkerParams.set('subject:Patient._tag', stageTag);
            if (hideTestPatients) {
              nullMarkerParams.set('subject:Patient._tag:not', TEST_PATIENT_TAG);
            }
            nullMarkerParams.set('_sort', '-_lastUpdated');
            nullMarkerParams.set('_count', String(nullCount));
            nullMarkerParams.set('_offset', String(nullOffset));

            const nullMarkerBundle = await medplum.search('Observation', nullMarkerParams);
            const nullPatientIds: string[] = [];
            for (const entry of nullMarkerBundle.entry ?? []) {
              const obs = entry.resource as { subject?: { reference?: string } } | undefined;
              const subjectRef = obs?.subject?.reference;
              if (subjectRef?.startsWith('Patient/')) {
                nullPatientIds.push(subjectRef.slice('Patient/'.length));
              }
            }

            if (nullPatientIds.length > 0) {
              const nullResult = await medplum.graphql(patientCsvForGraphQL(nullPatientIds.join(','), nullCount));
              nullRows = ((nullResult.data?.PatientList ?? []) as PatientResult[]).map(mapToEntry);
            }
          }
        }

        const merged = sort.direction === 'asc' ? [...nullRows, ...withValueRows] : [...withValueRows, ...nullRows];
        setTotal(totalTestingCount);
        setEntries(hideTestPatients ? merged.filter((e) => !e.isTestPatient) : merged);
        return;
      }

      // ── Default branch (name sort / no sort / search-only) ────────────────
      const nameParam = search ? ` name: "${search}"` : '';
      let sortParam = '-_lastUpdated';
      if (sort?.column === 'name') {
        sortParam = sort.direction === 'asc' ? 'given' : '-given';
      }

      if (hideTestPatients) {
        const patientParams = new URLSearchParams();
        patientParams.set('_tag', stageTag);
        addTestPatientExclusion(patientParams, true);
        if (search) {
          patientParams.set('name', search);
        }
        patientParams.set('_sort', sortParam);
        patientParams.set('_count', String(pageSize));
        patientParams.set('_offset', String(offset));
        patientParams.set('_total', 'accurate');

        const patientBundle = await medplum.search('Patient', patientParams);
        const patientIds = (patientBundle.entry ?? [])
          .map((entry: BundleEntry) => (entry.resource as Patient | undefined)?.id)
          .filter((id: string | undefined): id is string => Boolean(id));
        const hydration =
          patientIds.length > 0
            ? await medplum.graphql(patientCsvForGraphQL(patientIds.join(','), pageSize))
            : { data: { PatientList: [] } };
        const hydrated = (hydration.data?.PatientList ?? []) as PatientResult[];
        setTotal(patientBundle.total ?? 0);
        setEntries(hydrated.map(mapToEntry));
        return;
      }

      const result = await medplum.graphql(`
        query TestingQueue {
          PatientList(_tag: "${stageTag}"${nameParam} _count: ${pageSize} _offset: ${offset} _sort: "${sortParam}") {
            ${PATIENT_HYDRATION_FIELDS}
          }
          PatientConnection(_tag: "${stageTag}"${nameParam}) { count }
        }
      `);
      const patientList = (result.data?.PatientList ?? []) as PatientResult[];
      const connection = result.data?.PatientConnection as { count: number } | undefined;
      setTotal(connection?.count ?? 0);
      const mapped = patientList.map(mapToEntry);
      setEntries(hideTestPatients ? mapped.filter((e) => !e.isTestPatient) : mapped);
    } finally {
      setLoading(false);
    }
  }, [medplum, page, pageSize, search, sortKey, useObsSort, useSortAndSearch, patientCsvForGraphQL, hideTestPatients]);

  useEffect(() => {
    fetchEntries().catch(console.error);
  }, [fetchEntries]);

  return { entries, loading, total };
}
